import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NotificationDetailModal } from '../notifications/NotificationDetailModal';
import { announceNotificationsChanged } from '../notifications/useNotifications';
import { dashboardApi } from '../../services/api';
import type { DashboardNotificationItem, DashboardNotificationSeverity, PayrollShiftIssue } from '../../types';
import { useToast } from '../ui';

// The notification behind a check-in issue on the payroll page, the same one the bell shows.
// Opening it here doesn't mark it seen; the "Đánh dấu đã xem" button does.
export function StaffIssueModal({ issue, onClose }: { issue: PayrollShiftIssue | null; onClose: () => void }) {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [item, setItem] = useState<DashboardNotificationItem | null>(null);

    useEffect(() => {
        setItem(null);
        if (!issue?.activityId) return;
        let active = true;
        dashboardApi.getStaffNotification(issue.activityId)
            .then(response => active && setItem(response.data.data))
            .catch((error: any) => {
                if (!active) return;
                showToast(error?.message || 'Không mở được thông báo.', 'error');
                onClose();
            });
        return () => {
            active = false;
        };
        // onClose changes on every render of the parent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issue, showToast]);

    // Shows the change at once and puts it back if saving fails.
    const change = async (patch: Partial<DashboardNotificationItem>, save: () => Promise<unknown>, failed: string) => {
        if (!item) return;
        const before = item;
        setItem({ ...item, ...patch });
        try {
            await save();
            announceNotificationsChanged();
        } catch (error: any) {
            setItem(before);
            showToast(error?.message || failed, 'error');
        }
    };

    if (!issue || !item) return null;
    return (
        <NotificationDetailModal
            item={item}
            canChangePriority
            onClose={onClose}
            onGoTo={target => {
                onClose();
                navigate(target.href);
            }}
            onSetPriority={(target, severity: DashboardNotificationSeverity) => change(
                { severity },
                () => dashboardApi.setNotificationPriority(target.id, severity === (target.defaultSeverity ?? target.severity) ? null : severity),
                'Không đổi được mức độ.',
            )}
            onToggleStar={target => change({ starred: !target.starred }, () => dashboardApi.starNotification(target.id, !target.starred), 'Không gắn sao được.')}
            onMarkRead={target => change({ read: true }, () => dashboardApi.markNotificationsRead([target.id], true), 'Không đánh dấu được thông báo.')}
            onMarkUnread={target => change({ read: false }, () => dashboardApi.markNotificationsRead([target.id], false), 'Không đánh dấu được thông báo.')}
        />
    );
}
